/**
 * @fileoverview Connect a matter's typed attributes to their definitions and focused editors. Backend records own accepted values; the catalog owns allowed attribute shapes. `DemandSentAttributeForm.js` and `ResolvedAttributeForm.js` preserve domain-specific input context. Viewing or editing a draft attribute is separate from changing broader matter or provider state.
 */

import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import api from '../../api';
import ToastContext from '../../ToastContext';
import ResolvedAttributeForm from './ResolvedAttributeForm';
import { useSelector } from 'react-redux';
import DemandSentAttributeForm from './DemandSentAttributeForm';
import {
  AppButton,
  AppModal,
  AppSelect,
  AppDateInput,
  AppNumberInput,
  AppTextarea } from
'../shared';
import './Attributes.css';
import AppIcon from "../shared/AppIcon";

const ATTRIBUTE_PANEL_CONFIG = [
{
  key: 'dates',
  title: 'Dates',
  description: 'Deadlines, sent dates, and other tracked milestones.',
  icon: 'pi-calendar',
  tone: 'info',
  matches: (attribute) => attribute.Type === 'Date'
},
{
  key: 'numbers',
  title: 'Numbers',
  description: 'Amounts, counts, and percentage-style case values.',
  icon: 'pi-hashtag',
  tone: 'warning',
  matches: (attribute) => attribute.Type === 'Int' || attribute.Type === 'Float'
},
{
  key: 'text',
  title: 'Text',
  description: 'Freeform case details and longer stored notes.',
  icon: 'pi-file-edit',
  tone: 'neutral',
  matches: (attribute) => attribute.Type === 'Text'
}];




function buildOptionKey(attribute, type) {
  if (!attribute || !type) {
    return '';
  }
  return `${attribute}::${type}`;
}



function createEmptyAttributeFormState(overrides = {}) {
  return {
    visible: false,
    isEditing: false,
    attribute: null,
    type: null,
    value: null,
    oldValue: null,
    ...overrides
  };
}



function truncateText(text, length) {
  const safeText = String(text || '').trim();
  if (safeText.length > length) {
    return `${safeText.substring(0, length - 3)}...`;
  }
  return safeText;
}



function formatAttributeValue(attribute) {
  if (!attribute) {
    return 'No value';
  }

  if (attribute.Type === 'Date') {
    const date = new Date(attribute.Value);
    if (Number.isNaN(date.getTime())) {
      return 'No date';
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  if (attribute.Type === 'Int' || attribute.Type === 'Float') {
    const numericValue = Number(attribute.Value);
    if (Number.isFinite(numericValue)) {
      return numericValue.toLocaleString('en-US');
    }
  }

  const textValue = truncateText(attribute.Value, 180);
  return textValue || 'No value';
}



function Attributes({ matterNumber }) {
  const matter = useSelector((state) => state.caseDetails.matter);
  const [attributes, setAttributes] = useState([]);
  const [resolvedAttributeFormVisible, setResolvedAttributeFormVisible] = useState(false);
  const [demandSentAttributeFormVisible, setDemandSentAttributeFormVisible] = useState(false);
  const [attributeForm, setAttributeForm] = useState(createEmptyAttributeFormState());

  const fetchAttributes = useCallback(

  () => {
    api.get(`/case_attributes/${matterNumber}`)
    .then((response) => {
      setAttributes(Array.isArray(response.data) ? response.data : []);
    })
    .catch((error) => {
      console.error(error);
    });
  }, [matterNumber]);

  useEffect(() => {
    fetchAttributes();
  }, [fetchAttributes]);

  const openAttributeForm = useCallback(

  () => {
    setAttributeForm(createEmptyAttributeFormState({ visible: true }));
  }, []);

  const openAttributeFormInEditMode = useCallback(

  (attribute) => {
    setAttributeForm(createEmptyAttributeFormState({
      visible: true,
      isEditing: true,
      attribute: attribute.Attribute,
      type: attribute.Type,
      value: attribute.Value,
      oldValue: attribute.Value
    }));
  }, []);

  const attributePanels = useMemo(() => {
    return ATTRIBUTE_PANEL_CONFIG
    .map((panel) => ({
      ...panel,
      items: attributes.filter(panel.matches)
    }))
    .filter((panel) => panel.items.length > 0);
  }, [attributes]);

  const hasAttributes = attributes.length > 0;
  const showResolvedAction = matter?.Stage === 'Settled/Resolved' && matter?.Substage === 'Payments Complete/Fully Resolved';
  const showDemandSentAction = matter?.Stage === 'Demand Sent';

  return (
    <div className="case-attributes">
            <AttributeForm
        attributeForm={attributeForm}
        setAttributeForm={setAttributeForm}
        matterNumber={matterNumber}
        fetchAttributes={fetchAttributes} />

            <ResolvedAttributeForm
        visible={resolvedAttributeFormVisible}
        setVisible={setResolvedAttributeFormVisible}
        attributes={attributes}
        fetchAttributes={fetchAttributes} />

            <DemandSentAttributeForm
        visible={demandSentAttributeFormVisible}
        setVisible={setDemandSentAttributeFormVisible}
        attributes={attributes}
        fetchAttributes={fetchAttributes} />


            <section className="attributes-overview">
                <div className="attributes-overview-copy">
                    <div className="attributes-overview-eyebrow">Attributes</div>
                    <h2 className="attributes-overview-title">Case attributes</h2>
                    <p className="attributes-overview-description">
                        Stored dates, numbers, and text values tied to the matter, with quick edit access from one organized workspace.
                    </p>
                </div>

                <div className="attributes-actions">
                    <AppButton
            variant="secondary"
            icon="pi pi-plus"
            label="Add Attribute"
            className="attributes-action-button"
            onClick={openAttributeForm} />

                    {showResolvedAction &&
          <AppButton
            variant="secondary"
            icon="pi pi-check-circle"
            label="Resolution Attributes"
            className="attributes-action-button"
            onClick={() => setResolvedAttributeFormVisible(true)} />}


                    {showDemandSentAction &&
          <AppButton
            variant="secondary"
            icon="pi pi-send"
            label="Demand Attributes"
            className="attributes-action-button"
            onClick={() => setDemandSentAttributeFormVisible(true)} />}


                </div>
            </section>

            {!hasAttributes &&
      <section className="attributes-empty-state">
                    <div className="attributes-empty-icon">
                        <AppIcon icon="pi pi-sliders-h" aria-hidden="true" />
                    </div>
                    <div className="attributes-empty-title">No attributes yet</div>
                    <div className="attributes-empty-copy">
                        Add a case attribute to start tracking milestone dates, numeric values, or text-based case details here.
                    </div>
                </section>}


            {hasAttributes &&
      <div className="attributes-panel-grid">
                    {attributePanels.map((panel) =>
        <section key={panel.key} className="attributes-panel" data-tone={panel.tone}>
                            <div className="attributes-panel-header">
                                <div className="attributes-panel-copy">
                                    <div className="attributes-panel-title-row">
                                        <span className="attributes-panel-icon">
                                            <AppIcon icon={`pi ${panel.icon}`} aria-hidden="true" />
                                        </span>
                                        <h3 className="attributes-panel-title">{panel.title}</h3>
                                    </div>
                                    <p className="attributes-panel-description">{panel.description}</p>
                                </div>
                                <div className="attributes-panel-count">{panel.items.length}</div>
                            </div>

                            <ul className="attributes-list">
                                {panel.items.map((attribute, index) =>
            <li
              key={`${attribute.Attribute}-${attribute.Type}-${attribute.Value}-${index}`}
              className="attributes-list-item">

                                        <button
                type="button"
                className="attributes-item-button"
                onClick={() => openAttributeFormInEditMode(attribute)}>

                                            <div className="attributes-item-header">
                                                <span className="attributes-item-name">{attribute.Attribute}</span>
                                                <span className="attributes-item-edit">Edit</span>
                                            </div>
                                            <div className="attributes-item-value">{formatAttributeValue(attribute)}</div>
                                        </button>
                                    </li>
            )}
                            </ul>
                        </section>
        )}
                </div>}

        </div>);

}

export default Attributes;



function AttributeForm({ attributeForm, setAttributeForm, matterNumber, fetchAttributes }) {
  const showToast = useContext(ToastContext);
  const [attributeTypes, setAttributeTypes] = useState([]);
  const [selectedOptionKey, setSelectedOptionKey] = useState('');

  const loadAttributeTypes = useCallback(

  () => {
    api.get('/case_attribute_types')
    .then((response) => {
      setAttributeTypes(Array.isArray(response.data) ? response.data : []);
    })
    .catch((error) => {
      console.error(error);
    });
  }, []);

  useEffect(() => {
    loadAttributeTypes();
  }, [loadAttributeTypes]);

  useEffect(() => {
    setSelectedOptionKey(buildOptionKey(attributeForm.attribute, attributeForm.type));
  }, [attributeForm.attribute, attributeForm.type, attributeForm.visible]);

  const closeAttributeForm = useCallback(

  () => {
    setAttributeForm(createEmptyAttributeFormState());
  }, [setAttributeForm]);

  const submit = useCallback(

  () => {
    const payload = {
      Attribute: attributeForm.attribute,
      Type: attributeForm.type,
      Value: attributeForm.value,
      MatterNumber: matterNumber
    };

    if (attributeForm.isEditing) {
      payload.OldValue = attributeForm.oldValue;
    }

    const apiMethod = attributeForm.isEditing ? api.put : api.post;

    apiMethod('/case_attributes', payload)
    .then(() => {
      fetchAttributes();
      showToast({
        severity: 'success',
        summary: 'Successful',
        detail: attributeForm.isEditing ? 'Attribute updated' : 'Attribute added'
      });
      closeAttributeForm();
    })
    .catch((error) => {
      console.error('Error:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error.message });
    });
  }, [attributeForm.attribute, attributeForm.isEditing, attributeForm.oldValue, attributeForm.type, attributeForm.value, closeAttributeForm, fetchAttributes, matterNumber, showToast]);

  const deleteAttribute = useCallback(

  () => {
    api.delete('/case_attributes', {
      data: {
        Attribute: attributeForm.attribute,
        Type: attributeForm.type,
        Value: attributeForm.value,
        MatterNumber: matterNumber
      }
    })
    .then(() => {
      fetchAttributes();
      showToast({ severity: 'success', summary: 'Successful', detail: 'Attribute deleted' });
      closeAttributeForm();
    })
    .catch((error) => {
      console.error('Error:', error);
      showToast({ severity: 'error', summary: 'Error', detail: error.message });
    });
  }, [attributeForm.attribute, attributeForm.type, attributeForm.value, closeAttributeForm, fetchAttributes, matterNumber, showToast]);

  const updateAttributeValue = useCallback(

  (value) => {
    setAttributeForm((prev) => ({ ...prev, value }));
  }, [setAttributeForm]);

  const selectAttributeType = useCallback(

  (optionKey) => {
    setSelectedOptionKey(optionKey);
    const selectedType = attributeTypes.find(
      (option) => buildOptionKey(option.Attribute, option.Type) === optionKey
    );

    setAttributeForm((prev) => {
      if (!selectedType) {
        return { ...prev, attribute: null, type: null, value: null };
      }

      if (selectedType.Type === 'Date') {
        return {
          ...prev,
          attribute: selectedType.Attribute,
          type: selectedType.Type,
          value: new Date()
        };
      }

      return {
        ...prev,
        attribute: selectedType.Attribute,
        type: selectedType.Type,
        value: prev.isEditing ? prev.value : ''
      };
    });
  }, [attributeTypes, setAttributeForm]);

  const attributeTypeOptions = useMemo(() => {
    return attributeTypes.map((option) => ({
      label: option.Attribute,
      value: buildOptionKey(option.Attribute, option.Type)
    }));
  }, [attributeTypes]);

  const submitDisabled =
  !selectedOptionKey ||
  attributeForm.value === null ||
  attributeForm.value === undefined ||
  attributeForm.type === 'Text' && !String(attributeForm.value).trim();


  const footerContent =
  <div className="case-details-modal__actions">
            {attributeForm.isEditing &&
    <AppButton
      label="Delete"
      icon="pi pi-times"
      variant="danger"
      onClick={deleteAttribute} />}


            <AppButton
      label={attributeForm.isEditing ? 'Update' : 'Submit'}
      icon="pi pi-check"
      variant="primary"
      onClick={submit}
      autoFocus
      disabled={submitDisabled} />

        </div>;


  return (
    <AppModal
      title={attributeForm.isEditing ? 'Edit Attribute' : 'Add Attribute'}
      description="Add or update a stored case value with the same field structure used throughout case details."
      visible={attributeForm.visible}
      width="min(92vw, 760px)"
      onClose={closeAttributeForm}
      footer={footerContent}
      className="case-details-modal">

            <div className="case-details-modal__form">
                <section className="case-details-modal__section">
                    <div className="case-details-modal__section-header">
                        <div>
                            <h3 className="case-details-modal__section-title">Attribute Details</h3>
                            <p className="case-details-modal__section-description">
                                Choose the attribute type first, then enter the value that should be stored on the matter.
                            </p>
                        </div>
                    </div>
                    <div className="case-details-modal__section-body">
                        <div className="case-details-modal__field">
                            <label htmlFor="attributeType" className="case-details-modal__label">Attribute</label>
                            <AppSelect
                id="attributeType"
                value={selectedOptionKey}
                options={attributeTypeOptions}
                onChange={selectAttributeType}
                disabled={attributeForm.isEditing}
                placeholder="Select an attribute" />

                        </div>

                        {attributeForm.type &&
            <div className="case-details-modal__field">
                                <label htmlFor="attributeContent" className="case-details-modal__label">Value</label>
                                {attributeForm.type === 'Date' &&
              <AppDateInput
                id="attributeContent"
                value={attributeForm.value ? new Date(attributeForm.value) : null}
                onChange={updateAttributeValue} />}


                                {attributeForm.type === 'Int' &&
              <AppNumberInput
                id="attributeContent"
                value={attributeForm.value}
                onChange={updateAttributeValue}
                step={1} />}


                                {attributeForm.type === 'Float' &&
              <AppNumberInput
                id="attributeContent"
                value={attributeForm.value}
                onChange={updateAttributeValue}
                step={0.01} />}


                                {attributeForm.type === 'Text' &&
              <AppTextarea
                id="attributeContent"
                value={attributeForm.value}
                onChange={updateAttributeValue}
                rows={5} />}


                            </div>}

                    </div>
                </section>
            </div>
        </AppModal>);

}
